// src/components/common/Pagination/Pagination.tsx
import React from 'react';
import { Box, Typography, MenuItem, SelectChangeEvent } from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  FirstPage as FirstIcon,
  LastPage as LastIcon,
} from '@mui/icons-material';
import {
  PaginationContainer,
  PaginationInfo,
  PaginationText,
  PaginationControls,
  StyledFormControl,
  StyledSelect,
  PageButton,
  EllipsisButton,
} from './Pagination.styles';
import { getVisiblePaginationItems } from './getVisiblePaginationItems';
import PaginationGoToPageField from './PaginationGoToPageField';

export interface CommonPaginationProps {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (newPage: number) => void;
  onItemsPerPageChange: (event: SelectChangeEvent<unknown>) => void;
  hidePagination?: boolean;
  itemsPerPageOptions?: number[];
  className?: string;
  /** Sustituye "elementos" en los textos (p. ej. "etiquetas"). */
  itemLabelPlural?: string;
}

const CommonPagination: React.FC<CommonPaginationProps> = ({
  currentPage,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  hidePagination = false,
  itemsPerPageOptions = [5, 10, 25, 50], // Opciones del modelo por defecto
  className,
  itemLabelPlural = 'elementos',
}) => {
  // No renderizar si está oculto o no hay items
  if (hidePagination || totalItems === 0) {
    return null;
  }

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const startItem = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handleFirstPage = () => onPageChange(1);
  const handlePrevPage = () => onPageChange(Math.max(1, currentPage - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages, currentPage + 1));
  const handleLastPage = () => onPageChange(totalPages);

  return (
    <PaginationContainer className={className}>
      <PaginationInfo>
        <PaginationText>
          Mostrando {startItem}-{endItem} de {totalItems} {itemLabelPlural}
        </PaginationText>
      </PaginationInfo>

      <PaginationControls
        sx={{
          flex: 1,
          minWidth: 0,
          flexWrap: 'wrap',
        }}
      >
        <StyledFormControl size="small">
          <StyledSelect value={itemsPerPage} onChange={onItemsPerPageChange}>
            {itemsPerPageOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </StyledSelect>
        </StyledFormControl>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            flex: 1,
            minWidth: 0,
            flexWrap: 'wrap',
          }}
        >
          <PageButton onClick={handleFirstPage} disabled={currentPage === 1} size="small">
            <FirstIcon />
          </PageButton>
          <PageButton onClick={handlePrevPage} disabled={currentPage === 1} size="small">
            <PrevIcon />
          </PageButton>
          {getVisiblePaginationItems(totalPages, currentPage).map((page, index) => (
            <Box key={index}>
              {page === '...' ? (
                <EllipsisButton disabled>
                  <Typography variant="body2">...</Typography>
                </EllipsisButton>
              ) : (
                <PageButton
                  onClick={() => onPageChange(page as number)}
                  className={page === currentPage ? 'active' : ''}
                >
                  {page}
                </PageButton>
              )}
            </Box>
          ))}

          <PaginationGoToPageField
            currentPage={currentPage}
            totalPages={totalPages}
            onCommit={onPageChange}
          />
          <PageButton onClick={handleNextPage} disabled={currentPage === totalPages} size="small">
            <NextIcon />
          </PageButton>
          <PageButton onClick={handleLastPage} disabled={currentPage === totalPages} size="small">
            <LastIcon />
          </PageButton>
        </Box>
      </PaginationControls>
    </PaginationContainer>
  );
};

export default CommonPagination;
